import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MapPin, ShieldCheck, Wrench, Stethoscope, ArrowRight } from "lucide-react";
import WorkshopPicker from "@/components/WorkshopPicker";

/**
 * Maps a product (by keywords in its name) to the repair type that applies to
 * it. Each standard product has exactly one matching repair, so we pick it
 * automatically and do not show the user a "Velg reparasjon" picker.
 */
function repairTypeForProduct(product) {
  if (!product) return null;
  const n = `${product.name} ${product.description || ""}`.toLowerCase();
  if (/(skjerm|oled|display)/.test(n)) return "skjermbytte";
  if (/batteri/.test(n)) return "batteribytte";
  if (/(ladeport|lade-port|ladekontakt|lightning)/.test(n)) return "ladeport";
  if (/tastatur/.test(n)) return "tastatur";
  if (/(ssd|lagring|nvme)/.test(n)) return "ssd-oppgradering";
  if (/vannskade/.test(n)) return "vannskade";
  return "skjermbytte";
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [services, setServices] = useState([]);
  const [fixers, setFixers] = useState([]);
  const [pricingMap, setPricingMap] = useState({}); // fixer_id -> {service_id -> price}
  const [fixerId, setFixerId] = useState("");
  const [delivery, setDelivery] = useState("pickup");

  // diagnoseChoice: "repair-plus-diag" | "diag-only" | "no-diag" | null
  const [diagnoseChoice, setDiagnoseChoice] = useState(null);
  const [diagnoseFee, setDiagnoseFee] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      api.get("/services", { params: { category: r.data.category } }).then((rs) => {
        setServices(rs.data);
      });
    });
  }, [id]);

  // Pull pricing once per fixer-set load (handled inside WorkshopPicker)
  useEffect(() => {
    if (fixers.length === 0) return;
    api.get("/fixer-pricing").then((r) => {
      const map = {};
      for (const fp of r.data) {
        map[fp.fixer_id] = map[fp.fixer_id] || {};
        map[fp.fixer_id][fp.service_id] = fp.labor_price;
      }
      setPricingMap(map);
    });
  }, [fixers]);

  const autoService = useMemo(() => {
    if (!product || services.length === 0) return null;
    const rt = repairTypeForProduct(product);
    return services.find((s) => s.repair_type === rt) || services[0];
  }, [product, services]);

  // Diagnose fee at the selected workshop
  useEffect(() => {
    if (!fixerId) { setDiagnoseFee(0); return; }
    const diag = services.find((s) => s.repair_type === "diagnose");
    if (!diag) { setDiagnoseFee(499); return; }
    api
      .get("/fixer-pricing", { params: { fixer_id: fixerId, service_id: diag.id } })
      .then((r) => setDiagnoseFee(r.data[0]?.labor_price ?? 499))
      .catch(() => setDiagnoseFee(499));
  }, [fixerId, services]);

  const fixer = fixers.find((f) => f.id === fixerId);
  const laborPrice = fixer && autoService && pricingMap[fixer.id]?.[autoService.id];
  const baseTotal = (product?.price || 0) + (laborPrice || 0);
  const totalWithDiag =
    diagnoseChoice === "repair-plus-diag"
      ? baseTotal + diagnoseFee
      : diagnoseChoice === "diag-only"
        ? diagnoseFee
        : baseTotal;

  const proceed = async () => {
    if (!user) { nav("/logg-inn"); return; }
    if (user.role !== "customer") {
      toast.error("Bare kunder kan bestille reparasjon.");
      return;
    }
    if (!product || !fixerId) return;
    setBusy(true);
    try {
      if (diagnoseChoice === "diag-only") {
        // Pure diagnose draft — no part purchased
        const { data: draft } = await api.post("/checkout/draft/diagnostic", {
          device_category: product.category,
          device_model: (product.compatible_models || [])[0] || null,
          fixer_id: fixerId,
          delivery_method: delivery,
          symptoms: [],
        });
        nav(`/kassen?draft=${draft.id}`);
        return;
      }
      if (!autoService) return;
      const { data: draft } = await api.post("/checkout/draft/repair", {
        product_id: product.id,
        service_id: autoService.id,
        fixer_id: fixerId,
        delivery_method: delivery,
        include_diagnostic: diagnoseChoice === "repair-plus-diag",
      });
      nav(`/kassen?draft=${draft.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!product) return <div className="tk-container py-20 text-slate-500">Laster …</div>;

  const canShowDiagPrompt = Boolean(fixerId);

  return (
    <div className="tk-container py-16" data-testid="product-detail">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-[4/3] rounded-md overflow-hidden border border-[#E2E8F0] bg-white">
            {product.image && (
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            )}
          </div>

          <div className="mt-6 tk-card">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#4A7C59]" /> Kvalitetsgaranti
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Alle deler kommer med 12 måneders garanti og monteres av et godkjent
              Tekkdeler repair point. Delene er kvalitetssikret, men ikke originaldeler
              fra Apple.
            </p>
          </div>
        </div>

        <div>
          <p className="tk-overline mb-3 text-[#4A7C59]">{product.category}</p>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight mb-3" data-testid="product-name">
            {product.name}
          </h1>
          <p className="text-sm text-slate-500 mb-2">
            Passer: {(product.compatible_models || []).join(", ")}
          </p>
          <p className="text-2xl font-semibold mt-4">
            kr {product.price.toFixed(0)}{" "}
            <span className="text-sm font-normal text-slate-500">(del)</span>
          </p>
          <p className="text-sm text-slate-600 mt-6 leading-relaxed">{product.description}</p>

          <div className="tk-hairline my-8" />

          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Wrench size={18} /> Reparasjon
          </h2>
          <div
            className="px-4 py-3 rounded-md border border-[#E2E8F0] bg-slate-50"
            data-testid="auto-service-card"
          >
            <p className="font-medium text-sm" data-testid="auto-service-name">
              {autoService ? autoService.name : "Henter reparasjon …"}
            </p>
            {autoService?.description && (
              <p className="text-xs text-slate-500 mt-1">{autoService.description}</p>
            )}
          </div>

          <div className="tk-hairline my-8" />

          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <MapPin size={18} /> Velg verksted
          </h2>
          <WorkshopPicker
            extraParams={{ product_id: id }}
            onLoad={setFixers}
            selectedId={fixerId}
            onSelect={setFixerId}
            renderItem={(f, isSelected, onSelect) => (
              <button
                key={f.id}
                type="button"
                onClick={onSelect}
                className={`w-full text-left px-4 py-3 rounded-md border flex items-center justify-between ${isSelected ? "border-[#0B1C30] bg-slate-50" : "border-[#E2E8F0] bg-white hover:bg-slate-50"}`}
                data-testid={`fixer-option-${f.id}`}
              >
                <div>
                  <p className="font-medium text-sm">{f.display_name}</p>
                  <p className="text-xs text-slate-500">
                    {f.address}, {f.postal_code}
                    {typeof f.distance_km === "number" && ` · ${f.distance_km} km`}
                  </p>
                </div>
                <span className="text-xs text-slate-500">
                  {autoService && pricingMap[f.id]?.[autoService.id]
                    ? `Arbeid: kr ${pricingMap[f.id][autoService.id].toFixed(0)}`
                    : "Pris ved bestilling"}
                </span>
              </button>
            )}
          />

          <div className="tk-hairline my-8" />

          <h2 className="text-lg font-semibold mb-3">Levering</h2>
          <div className="flex gap-2">
            {[
              { k: "pickup", l: "Lever inn selv" },
              { k: "shipping", l: "Send med Bring" },
            ].map((d) => (
              <button
                key={d.k}
                onClick={() => setDelivery(d.k)}
                className={`px-4 py-2 rounded-md text-sm border ${delivery === d.k ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700"}`}
                data-testid={`delivery-${d.k}`}
              >
                {d.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Full-width DIAGNOSE PROMPT step — shows only once a workshop is picked.
          The customer must make a choice (one of the 3 buttons) before reaching the
          summary/CTA below it. */}
      {canShowDiagPrompt && (
        <section
          className="mt-12 border-y border-[#E2E8F0] bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-10"
          data-testid="diagnose-prompt-step"
        >
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <Stethoscope className="text-[#4A7C59]" size={24} />
              <h2 className="text-2xl font-semibold leading-tight">
                Er du usikker på hva som er galt?
              </h2>
            </div>
            <p className="text-sm text-slate-600 max-w-xl mb-8 leading-relaxed">
              Vi kan diagnostisere enheten din først — ingen reparasjon igangsettes uten
              din godkjenning.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <button
                type="button"
                onClick={() => setDiagnoseChoice("repair-plus-diag")}
                className={`text-left p-6 rounded-md border ${diagnoseChoice === "repair-plus-diag" ? "border-[#0B1C30] bg-white" : "border-[#E2E8F0] bg-white hover:border-[#0B1C30]/40"}`}
                data-testid="diag-choice-repair-plus"
              >
                <p className="font-semibold mb-1">Reparer + diagnostiser</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Fortsett med valgt del og inkluder en diagnose. Verkstedet bekrefter
                  om delen stemmer før arbeidet starter.
                </p>
                <p className="text-xs text-[#0B1C30] mt-3">+ kr {diagnoseFee.toFixed(0)} diagnose</p>
              </button>
              <button
                type="button"
                onClick={() => setDiagnoseChoice("diag-only")}
                className={`text-left p-6 rounded-md border ${diagnoseChoice === "diag-only" ? "border-[#0B1C30] bg-white" : "border-[#E2E8F0] bg-white hover:border-[#0B1C30]/40"}`}
                data-testid="diag-choice-diag-only"
              >
                <p className="font-semibold mb-1">Bare diagnose først</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Send inn enheten for diagnose. Du velger reparasjon etterpå basert på
                  rapporten.
                </p>
                <p className="text-xs text-[#0B1C30] mt-3">kr {diagnoseFee.toFixed(0)} diagnose</p>
              </button>
            </div>

            <button
              type="button"
              onClick={() => setDiagnoseChoice("no-diag")}
              className={`text-sm w-full text-left px-5 py-4 rounded-md border ${diagnoseChoice === "no-diag" ? "border-[#0B1C30] bg-white" : "border-[#E2E8F0] bg-white hover:border-[#0B1C30]/40"}`}
              data-testid="diag-choice-no"
            >
              Nei takk, fortsett til betaling →
            </button>
          </div>
        </section>
      )}

      {/* Summary + final CTA — only enabled once a diagnose choice is made */}
      {canShowDiagPrompt && (
        <div className="max-w-3xl mx-auto mt-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs text-slate-500">Estimert totalpris</p>
            <p className="text-3xl font-semibold" data-testid="estimated-total">
              kr {totalWithDiag.toFixed(0)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {diagnoseChoice === "diag-only"
                ? "Kun diagnose"
                : `kr ${product.price.toFixed(0)} (del)${laborPrice ? ` + kr ${laborPrice.toFixed(0)} (arbeid)` : ""}${diagnoseChoice === "repair-plus-diag" ? ` + kr ${diagnoseFee.toFixed(0)} (diagnose)` : ""}`}
            </p>
          </div>
          <button
            onClick={proceed}
            disabled={busy || !diagnoseChoice}
            className="tk-btn-primary !py-3 !px-6"
            data-testid="book-now-btn"
          >
            {busy ? "Reserverer …" : "Til kassen"} <ArrowRight size={16} />
          </button>
        </div>
      )}

      {!canShowDiagPrompt && (
        <p className="text-sm text-slate-500 max-w-3xl mt-10">
          Velg et verksted over for å fortsette.{" "}
          <Link to="/butikk" className="underline">Tilbake til butikken</Link>
        </p>
      )}
    </div>
  );
}
